import { createAction } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { deleteCookie, getCookie, setCookie } from '../../utils/cookies';

export const apiUrl = process.env.REACT_APP_API_URL;
export const urlDev = process.env.REACT_APP_DEV_URL;
export const urlProd = process.env.REACT_APP_PROD_URL;

export const tokenRefreshed = createAction('user/tokenRefreshed');

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiUrl,
  prepareHeaders: (headers, { getState }) => {
    headers.set('Accept', 'application/json');

    // Endpoints pass the token they rendered with; it may be stale after a refresh.
    const token = getState().user?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

// Per-id tags of a realty that changed owner or `can` flags (claim, reassign, auto-claim).
export const realtyTags = id => [
  { type: 'Realties', id },
  { type: 'SearchRealties', id },
  { type: 'SearchRealtiesMap', id },
  { type: 'EventLog', id: 'LIST' },
];

// JWT `sub` = agent id.
export const tokenSubject = token => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).sub ?? null;
  } catch {
    return null;
  }
};

let refreshPromise = null;
let isLeaving = false;

const leave = (url, ownTokens) => {
  if (isLeaving) return;
  isLeaving = true;

  if (ownTokens.includes(getCookie('token'))) deleteCookie('token');
  window.location.assign(url);
};

// Only a 401 logs out; network errors, 5xx and 429 keep the session.
const refreshToken = async (apiCtx, extraOptions) => {
  const result = await rawBaseQuery(
    { url: 'api/auth/refresh', method: 'POST' },
    apiCtx,
    extraOptions
  );
  const token = result.data?.access_token;
  if (!token) return { loggedOut: result.error?.status === 401 };

  setCookie('token', token, result.data.refresh_expires_in);
  apiCtx.dispatch(tokenRefreshed(token));
  return { token };
};

// On 401: cookie gone → /login; another agent in the cookie → reload;
// newer token → retry; else one shared refresh and retry.
const baseQuery = async (args, apiCtx, extraOptions) => {
  const tokenUsed = apiCtx.getState().user?.token;
  let result = await rawBaseQuery(args, apiCtx, extraOptions);

  if (result.error?.status !== 401 || !tokenUsed || isLeaving) {
    return result;
  }

  const cookieToken = getCookie('token');
  if (!cookieToken) {
    leave('/login', [tokenUsed]);
    return result;
  }
  if (tokenSubject(cookieToken) !== tokenSubject(tokenUsed)) {
    leave(window.location.href, []);
    return result;
  }

  if (apiCtx.getState().user?.token === tokenUsed && cookieToken !== tokenUsed) {
    apiCtx.dispatch(tokenRefreshed(cookieToken));
  }
  if (apiCtx.getState().user?.token !== tokenUsed) {
    result = await rawBaseQuery(args, apiCtx, extraOptions);
    if (result.error?.status !== 401) return result;
  }

  refreshPromise ??= refreshToken(apiCtx, extraOptions).finally(() => {
    refreshPromise = null;
  });
  const refreshed = await refreshPromise;

  if (refreshed.token) {
    result = await rawBaseQuery(args, apiCtx, extraOptions);
    if (result.error?.status !== 401) return result;
  }
  if (refreshed.token || refreshed.loggedOut) {
    leave('/login', [tokenUsed, apiCtx.getState().user?.token]);
  }

  return result;
};

export const api = createApi({
  reducerPath: 'splitApi',
  baseQuery,
  endpoints: () => ({}),
  tagTypes: [
    'User',
    'Clients',
    'Client',
    'Request',
    'Requests',
    'Visit',
    'Visits',
    'RealtyVisits',
    'Realties',
    'Realty',
    'Agents',
    'AgentsAssistants',
    'AgentsAssistantsUser',
    'Countries',
    'Cities',
    'Districts',
    'Streets',
    'Residences',
    'SearchRealties',
    'SearchRealtiesMap',
    'SearchRealtiesInput',
    'Documents',
    'Photos',
    'Reminders',
    'RemindersCounters',
    'Emails',
    'ParserData',
    'ParserSessionsHistory',
    'TaskAdverts',
    'SpiderList',
    'EventLog',
    'ImportBatch',
    'ImportBatchList',
    'ScopePreview',
    'RecordsPreview',
    'AvailableCount',
    'Diff',
    'BatchAudit',
    'ImportRule',
    'ImportRuleList',
    'RulePreviewRecords',
    'RuleHistory',
    'QueueDashboard',
  ],
});
